(() => {
  const configElement = document.getElementById('pfm-review-threshold-config');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch { return; }
  if (!/^\d+$/.test(config.productId) || config.minimum !== 50) return;

  const selector = `.yotpo-widget-instance[data-yotpo-product-id="${config.productId}"]`;
  const seed = Number.isInteger(config.seedCount) && config.seedCount >= 0 ? config.seedCount : 0;
  let apiCount = null;
  let observer;
  let frame = 0;
  let request;
  let below = null;
  const requestedTabs = new WeakSet();
  const switchedWidgets = new WeakSet();
  const initializedQuestions = new WeakSet();
  const pendingQuestions = new Map();

  function parseCount(text) {
    const match = String(text || '').trim().match(/(\d[\d\s,.\u00a0\u202f]*)\s*([km])?/i);
    if (!match) return null;
    const count = match[2]
      ? Number(match[1].replace(/\s/g, '').replace(',', '.')) * (match[2].toLowerCase() === 'k' ? 1000 : 1000000)
      : Number(match[1].replace(/\D/g, ''));
    return Number.isFinite(count) && count >= 0 ? Math.floor(count) : null;
  }

  function widgetCount() {
    for (const instance of document.querySelectorAll(`#main ${selector}`)) {
      const text = instance.querySelector('.yotpo-sr-bottom-line-text');
      const count = parseCount(text?.textContent);
      if (count !== null) return count;
    }
    return null;
  }

  function initializeQuestions(widget, questionsTab) {
    const panel = widget.querySelector('#yotpo-tabs-main-widget-tab-panel-1');
    if (!panel || initializedQuestions.has(panel) || pendingQuestions.has(panel)) return;
    if (questionsTab.getAttribute('aria-selected') !== 'true') return;
    const instanceId = panel.getAttribute('data-yotpo-instance-id');
    if (!/^\d+$/.test(instanceId || '') || panel.getAttribute('data-yotpo-product-id') !== config.productId) return;
    if (panel.getAttribute('data-yotpo-element-loaded') === 'true' || panel.childElementCount || panel.textContent.trim()) return;

    // Yotpo normally initializes its embedded Q&A after 50 ms. Allow that native
    // action to finish before recovering an otherwise empty, selected panel.
    const timer = setTimeout(() => {
      pendingQuestions.delete(panel);
      if (!below || panel.isConnected === false || questionsTab.getAttribute('aria-selected') !== 'true') return;
      if (panel.getAttribute('data-yotpo-product-id') !== config.productId || panel.getAttribute('data-yotpo-instance-id') !== instanceId) return;
      if (panel.getAttribute('data-yotpo-element-loaded') === 'true' || panel.childElementCount || panel.textContent.trim()) return;
      const container = window.yotpoWidgetsContainer;
      if (typeof container?.initWidget !== 'function') return;
      initializedQuestions.add(panel);
      try {
        // This is the same public initializer used by Yotpo's Q&A component.
        Promise.resolve(container.initWidget(instanceId, panel)).catch(() => {});
      } catch {
        // One attempt per panel; the existing tab and other widgets stay usable.
      }
    }, 500);
    pendingQuestions.set(panel, timer);
  }

  function update() {
    frame = 0;
    const count = apiCount ?? widgetCount() ?? seed;
    const nextBelow = count < config.minimum;
    const becameVisible = below === true && !nextBelow;
    below = nextBelow;
    const state = below ? 'below' : 'visible';
    if (document.documentElement.dataset.pfmReviewsState !== state) {
      document.documentElement.dataset.pfmReviewsState = state;
    }
    document.documentElement.dataset.pfmReviewCount = String(count);

    for (const instance of document.querySelectorAll(`#main ${selector}`)) {
      const widget = instance.querySelector('.yotpo-reviews-main-widget');
      if (!widget) continue;
      const reviewsTab = widget.querySelector('#yotpo-tabs-main-widget-tab-0');
      const questionsTab = widget.querySelector('#yotpo-tabs-main-widget-tab-1');
      if (!reviewsTab || !questionsTab) continue;
      if (below) {
        // Use Yotpo's own tab action so its separate Q&A widget loads normally.
        if (questionsTab.getAttribute('aria-selected') === 'true') {
          requestedTabs.delete(questionsTab);
          initializeQuestions(widget, questionsTab);
        } else if (!questionsTab.disabled && !requestedTabs.has(questionsTab)) {
          requestedTabs.add(questionsTab);
          switchedWidgets.add(widget);
          questionsTab.click();
        }
      } else if (becameVisible && switchedWidgets.has(widget)) {
        switchedWidgets.delete(widget);
        if (questionsTab.getAttribute('aria-selected') === 'true') reviewsTab.click();
      }
    }
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(update);
  }

  function observe() {
    observer?.disconnect();
    const main = document.getElementById('main');
    if (!main) return;
    observer = new MutationObserver(schedule);
    observer.observe(main, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['aria-selected']
    });
    schedule();
  }

  async function loadCount() {
    // The public Reviews loader identifies the store; no secret or theme ID is stored here.
    const loader = [...document.scripts].map(script => {
      try {
        const url = new URL(script.src);
        if (url.hostname !== 'cdn-widgetsrepository.yotpo.com') return null;
        return url.pathname.match(/^\/v1\/loader\/([\w-]{30,})$/)?.[1] || null;
      } catch { return null; }
    }).find(Boolean);
    if (!loader) return;
    request = new AbortController();
    const timeout = setTimeout(() => request.abort(), 8000);
    try {
      const url = `https://api-cdn.yotpo.com/v3/storefront/store/${encodeURIComponent(loader)}/product/${encodeURIComponent(config.productId)}/ratings`;
      const response = await fetch(url, { credentials: 'omit', signal: request.signal });
      if (!response.ok) return;
      const payload = await response.json();
      const count = payload?.bottomline?.totalReviews;
      if (Number.isInteger(count) && count >= 0) {
        apiCount = count;
        schedule();
      }
    } catch {
      // If the service is unavailable, use the existing widget count or Shopify's seed.
    } finally {
      clearTimeout(timeout);
    }
  }

  function start() {
    observe();
    loadCount();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', observe);
  window.addEventListener('pagehide', () => {
    observer?.disconnect();
    request?.abort();
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    for (const timer of pendingQuestions.values()) clearTimeout(timer);
    pendingQuestions.clear();
  });
  window.addEventListener('pageshow', event => { if (event.persisted) observe(); });
})();
